import { IPlan, initialValues } from '.';
import { quote } from '@components/widgets/dynamic-cardview/QuotesCards';
import {
  enumListAsRecordObject,
  lookupListAsRecordObject,
} from '@utils/graph-utils';
import {
  isEmpty,
  isValidEmail,
  validatePhoneNumber,
} from '@utils/validationUtils';
import { steps } from './content';

/** Dev-only: trace breakdown field mapping from GraphQL plans */
export const logMotorBreakdownDebug = (
  label: string,
  payload: Record<string, unknown>
) => {
  if (process.env.NODE_ENV === 'production') return;
  console.log('[MotorBreakdown]', label, payload);
};

/** Dev-only: trace calculate, dirty-state, and MBI-specific flows */
export const logMotorDebug = (
  category: 'dirty' | 'calculate' | 'mbi' | 'field-reset' | 'data-load',
  label: string,
  payload: Record<string, unknown>
) => {
  if (process.env.NODE_ENV === 'production') return;
  console.log(`[Motor:${category}]`, label, payload);
};

export const summarizePlanDetailsForLog = (
  planDetails: Array<Record<string, unknown>> | undefined,
  productsLov?: Record<string, string>
) =>
  (planDetails ?? []).map((pd, i) => ({
    index: i,
    product: pd?.product,
    productTitle: productsLov?.[pd?.product as string],
    towingService: pd?.towingService,
    breakdownServices: pd?.breakdownServices,
    vignetteCode: pd?.vignetteCode,
    vignette: pd?.vignette,
  }));

export const CASH_PLUS_AGENCY_NAME = 'Cash Plus';
export const DEFAULT_CAR_NATIONALITY = 'Lebanese';

export const isCashPlusAgency = (agencyName?: string) =>
  agencyName === CASH_PLUS_AGENCY_NAME;

export function entityToApplicationDetails(data: any): any {
  const applicationInfo =
    data?.Insurance?.entities?.application?.views?.Insurance_all?.properties;
  const motorDetails =
    data?.Insurance?.entities?.application?.views?.Insurance_all?.properties
      ?.MotorDetailsID?.views?.Insurance_all?.properties;
  const customerDetails =
    data?.Insurance?.entities?.application?.views?.Insurance_all?.properties
      ?.ApplicationContactDetailsID?.views?.Insurance_all?.properties;
  const agencyName =
    applicationInfo?.AssignedAgencyID?.views?.Insurance_all?.properties
      ?.AgencyName || '';
  const planDetails: Record<string, string | number>[] = [];
  data?.Insurance?.entities?.application?.views?.Insurance_all?.properties?.MotorDetailsID?.queries?.insurance?.plans?.forEach(
    (element: IPlan, planIndex: number) => {
      const breakdownFromApi = element?.input_BreakdownService;
      logMotorBreakdownDebug('entityToApplicationDetails plan', {
        planIndex,
        product: element?.input_Product?.Code,
        input_BreakdownService: breakdownFromApi,
        mappedId: breakdownFromApi?.Id,
      });
      planDetails.push({
        planId: element?.input_Id,
        name: element?.quote_Name,
        product: element?.input_Product?.Code,
        towingService: element?.input_TowingService?.Id,
        breakdownServices: breakdownFromApi?.Id,
        vignetteCode: element?.input_VignetteCode,
        vignette: element?.input_Vignette,
        coverageType: element?.input_CoverageType,
        carValue: element?.input_CarValue,
        includeAgencyRepair: element?.input_IncludeAgencyRepair,
      });
    }
  );

  return {
    applicationName: applicationInfo?.ApplicationName,
    effectiveDate: applicationInfo?.EffectiveDate,
    expiryDate: applicationInfo?.ExpiryDate,
    isRenewal: applicationInfo?.IsRenewal || initialValues.isRenewal,
    renewalNumber: applicationInfo?.RenewalNumber || '',
    carNationality:
      motorDetails?.CarNationality?.Id ||
      (isCashPlusAgency(agencyName) ? DEFAULT_CAR_NATIONALITY : ''),
    plateCode: motorDetails?.PlateCode?.Id || '',
    plateNumber: motorDetails?.PlateNumber || '',
    yearOfMake: isEmpty(motorDetails?.YearOfMake)
      ? ''
      : motorDetails?.YearOfMake,
    motor: motorDetails?.Motor || '',
    weight: motorDetails?.Weight || '',
    engineSize: motorDetails?.EngineSize || '',
    seats: isEmpty(motorDetails?.Seats) ? '' : motorDetails?.Seats,
    chassis: motorDetails?.Chassis || '',
    horsePower: isEmpty(motorDetails?.HorsePower)
      ? ''
      : motorDetails?.HorsePower,
    usageType: motorDetails?.UsageType?.Id || '',
    bodyType: motorDetails?.BodyType?.Id || '',
    engineType: motorDetails?.EngineType?.Id || '',
    brand: motorDetails?.Brand?.Id || '',
    model: motorDetails?.Model?.Id || '',
    status: applicationInfo?.Status || '',
    statusReason: applicationInfo?.StatusReason || '',
    lineOfBusiness: applicationInfo?.LineOfBusiness?.Title || '',
    agencyName,
    agentName:
      applicationInfo?.AssignedAgentID?.views?.Insurance_all?.properties
        ?.FirstName +
      ' ' +
      applicationInfo?.AssignedAgentID?.views?.Insurance_all?.properties
        ?.LastName,
    customerName:
      applicationInfo?.ApplicationContactDetailsID?.views?.Insurance_all
        ?.properties?.FullName,
    createdBy: `${data?.Insurance?.entities?.application?.queries?.insurance?.relatedUsers[0]?.createdBy_firstName} ${data?.Insurance?.entities?.application?.queries?.insurance?.relatedUsers[0]?.createdBy_lastName}`,
    createdOn: applicationInfo?.createdOn,
    modifiedBy: `${data?.Insurance?.entities?.application?.queries?.insurance?.relatedUsers[0]?.modifiedBy_firstName} ${data?.Insurance?.entities?.application?.queries?.insurance?.relatedUsers[0]?.modifiedBy_lastName}`,
    modifiedOn: applicationInfo?.modifiedOn,
    nameOnLicenseEn: motorDetails?.NameOnLicenseEn || '',
    nameOnLicenseAr: motorDetails?.NameOnLicenseAr || '',
    email: customerDetails?.Email || '',
    mobileNumber: customerDetails?.MobileNumber || '',
    country: customerDetails?.Address?.Country?.Id || initialValues.country,
    city: customerDetails?.Address?.City || '',
    street: customerDetails?.Address?.Street || '',
    building: customerDetails?.Address?.Building || '',
    planDetails: planDetails || '',
    title: customerDetails?.Title?.Id || initialValues.title,
    fullName: customerDetails?.FullName || '',
    firstName: customerDetails?.FirstName || '',
    middleName: customerDetails?.MiddleName || '',
    lastName: customerDetails?.LastName || '',
    phoneType: customerDetails?.PhoneType || initialValues.phoneType,
  };
}

export function extractLovs(data: any): Record<string, Record<string, string>> {
  const lovs: Record<string, Record<string, string>> = {
    products: {},
    towingServices: {},
    breakdownServices: {},
    coverageTypes: {},
    countries: {},
    plateCodes: {},
    usageTypes: {},
    bodyTypes: {},
    engineTypes: {},
    brands: {},
    models: {},
    carNationalities: {},
    renewalStatus: {},
    phoneTypes: {},
    agencyRepairs: {},
  };
  lovs['products'] = lookupListAsRecordObject(
    data?.Insurance?.lookups?.products
  );
  lovs['towingServices'] = lookupListAsRecordObject(
    data?.Insurance?.lookups?.towingServices
  );
  lovs['breakdownServices'] = lookupListAsRecordObject(
    data?.Insurance?.lookups?.breakdownServices
  );
  lovs['coverageTypes'] = enumListAsRecordObject(
    data.Insurance_CoverageTypes.enumValues
  );
  lovs['plateCodes'] = lookupListAsRecordObject(
    data?.Insurance?.lookups?.plateCodes
  );
  lovs['usageTypes'] = lookupListAsRecordObject(
    data?.Insurance?.lookups?.usageTypes
  );
  lovs['bodyTypes'] = lookupListAsRecordObject(
    data?.Insurance?.lookups?.bodyTypes
  );
  lovs['carNationalities'] = lookupListAsRecordObject(
    data?.Insurance?.lookups?.carNationalities
  );
  lovs['engineTypes'] = lookupListAsRecordObject(
    data?.Insurance?.lookups?.engineTypes
  );
  lovs['brands'] = lookupListAsRecordObject(data?.Insurance?.lookups?.brands);

  lovs['countries'] = lookupListAsRecordObject(
    data?.Insurance?.lookups?.countries
  );

  lovs['renewalStatus'] = enumListAsRecordObject(
    data.Insurance_RenewalStatus.enumValues
  );

  lovs['titles'] = lookupListAsRecordObject(data?.Insurance?.lookups?.titles);

  lovs['phoneTypes'] = enumListAsRecordObject(
    data?.Insurance_PhoneType?.enumValues
  );

  lovs['agencyRepairs'] = enumListAsRecordObject(
    data?.Insurance_AgencyRepairs?.enumValues
  );

  return lovs;
}

export function graphqlEntityToQuoteDetails(
  data: any,
  index: number,
  currencySymbol: string,
  lob: string
): quote {
  const plan =
    data?.Insurance?.entities?.application?.views?.Insurance_all?.properties
      ?.MotorDetailsID?.queries?.insurance?.plans[index];
  const breakdownFromApi = plan?.input_BreakdownService;
  logMotorBreakdownDebug('graphqlEntityToQuoteDetails', {
    index,
    product: plan?.input_Product?.Code,
    input_BreakdownService: breakdownFromApi,
    breakdownServicesTitle: breakdownFromApi?.Title,
    planKeys: plan ? Object.keys(plan) : [],
  });

  return {
    lineOfBusiness: lob,
    id: data?.Insurance?.entities?.application?.views?.Insurance_all?.properties
      ?.MotorDetailsID?.queries?.insurance?.plans[index]?.quote_Id,
    title:
      data?.Insurance?.entities?.application?.views?.Insurance_all?.properties
        ?.MotorDetailsID?.queries?.insurance?.plans[index]?.quote_Name,
    status:
      data?.Insurance?.entities?.application?.views?.Insurance_all?.properties
        ?.MotorDetailsID?.queries?.insurance?.plans[index]?.quote_Status,
    statusReason:
      data?.Insurance?.entities?.application?.views?.Insurance_all?.properties
        ?.MotorDetailsID?.queries?.insurance?.plans[index]?.quote_StatusReason,
    createdOn:
      data?.Insurance?.entities?.application?.views?.Insurance_all?.properties
        ?.createdOn,
    modifiedOn:
      data?.Insurance?.entities?.application?.views?.Insurance_all?.properties
        ?.modifiedOn,
    hasCoverage:
      !isEmpty(
        data?.Insurance?.entities?.application?.views?.Insurance_all?.properties
          ?.MotorDetailsID
      ) &&
      data?.Insurance?.entities?.application?.views?.Insurance_all?.properties
        ?.LineOfBusiness?.Title == 'Motor',
    coverageAmount:
      data?.Insurance?.entities?.application?.views?.Insurance_all?.properties
        ?.MotorDetailsID?.queries?.insurance?.plans[index]
        ?.motorquote_SumInsured,
    coverageAmountCurrencySymbol: currencySymbol,
    premiumAmount:
      data?.Insurance?.entities?.application?.views?.Insurance_all?.properties
        ?.MotorDetailsID?.queries?.insurance?.plans[index]
        ?.quote_TotalBasePremium,
    premiumAmountCurrencySymbol: currencySymbol,
    carValue:
      data?.Insurance?.entities?.application?.views?.Insurance_all?.properties
        ?.MotorDetailsID?.queries?.insurance?.plans[index]?.input_CarValue,
    carValueCurrencySymbol: currencySymbol,
    vignetteCode:
      data?.Insurance?.entities?.application?.views?.Insurance_all?.properties
        ?.MotorDetailsID?.queries?.insurance?.plans[index]?.input_VignetteCode,
    vignette:
      data?.Insurance?.entities?.application?.views?.Insurance_all?.properties
        ?.MotorDetailsID?.queries?.insurance?.plans[index]?.input_Vignette,
    towingServiceTitle:
      data?.Insurance?.entities?.application?.views?.Insurance_all?.properties
        ?.MotorDetailsID?.queries?.insurance?.plans[index]?.input_TowingService
        ?.Title,
    breakdownServicesTitle:
      data?.Insurance?.entities?.application?.views?.Insurance_all?.properties
        ?.MotorDetailsID?.queries?.insurance?.plans[index]
        ?.input_BreakdownService?.Title,
    policyId:
      data?.Insurance?.entities?.application?.views?.Insurance_all?.properties
        ?.MotorDetailsID?.queries?.insurance?.plans[index]?.policy_Id,
    fullPolicyNumber:
      data?.Insurance?.entities?.application?.views?.Insurance_all?.properties
        ?.MotorDetailsID?.queries?.insurance?.plans[index]
        ?.policy_FullPolicyNumber,
    disabled:
      data?.Insurance?.entities?.application?.views?.Insurance_all?.properties
        ?.MotorDetailsID?.queries?.insurance?.plans[index]?.quote_Status ==
      'REFER_TO_UW_DEPARTMENT',
    product: {
      Code: data?.Insurance?.entities?.application?.views?.Insurance_all
        ?.properties?.MotorDetailsID?.queries?.insurance?.plans[index]
        ?.input_Product?.Code,
      Title:
        data?.Insurance?.entities?.application?.views?.Insurance_all?.properties
          ?.MotorDetailsID?.queries?.insurance?.plans[index]?.input_Product
          ?.Title,
    },
  };
}

const BODY_TYPE_SHOW_WHEN: Record<
  string,
  { usageTypes: string[]; requiresLebanese: boolean }
> = {
  '4x4': { usageTypes: ['Private'], requiresLebanese: false },
  SEDAN: { usageTypes: ['Private'], requiresLebanese: false },
  STATION: { usageTypes: ['Private'], requiresLebanese: false },
  SUV: { usageTypes: ['Private'], requiresLebanese: false },
  BUS: { usageTypes: ['Private', 'Commercial'], requiresLebanese: true },
  MINI_BUS: { usageTypes: ['Private', 'Commercial'], requiresLebanese: true },
  MINIVAN: { usageTypes: ['Private', 'Commercial'], requiresLebanese: true },
  MOTORCYCLE: {
    usageTypes: ['Private', 'Commercial'],
    requiresLebanese: true,
  },
  PICKUP: { usageTypes: ['Private', 'Commercial'], requiresLebanese: true },
  SCHOOL_BUS: {
    usageTypes: ['Private', 'Commercial'],
    requiresLebanese: true,
  },
  VAN: { usageTypes: ['Private', 'Commercial'], requiresLebanese: true },
  CRANE: { usageTypes: ['Commercial'], requiresLebanese: true },
  F_TANKER: { usageTypes: ['Commercial'], requiresLebanese: true },
  MICROBUS: { usageTypes: ['Commercial'], requiresLebanese: true },
  TOWING: { usageTypes: ['Commercial'], requiresLebanese: true },
  TRUCK: { usageTypes: ['Commercial'], requiresLebanese: true },
  TAXI: { usageTypes: ['Public'], requiresLebanese: true },
};

export function filterBodyTypeLOVs(
  usageType = '',
  bodyType: Record<string, string>,
  carNationality = ''
) {
  if (!bodyType || !usageType) {
    return {};
  }

  const isLebanese =
    !carNationality || carNationality === DEFAULT_CAR_NATIONALITY;

  return Object.fromEntries(
    Object.entries(bodyType).filter(([key]) => {
      const rule = BODY_TYPE_SHOW_WHEN[key];
      if (!rule) {
        return false;
      }
      if (!rule.usageTypes.includes(usageType)) {
        return false;
      }
      if (rule.requiresLebanese && !isLebanese) {
        return false;
      }
      return true;
    })
  );
}

export function filterTowingServiceLOVs(
  bodyType: string,
  towingServices: Record<string, string>,
  weight = 0
) {
  if (!towingServices) {
    return towingServices;
  }

  const unlimitedTowingBodyTypes = ['4x4', 'SEDAN', 'STATION', 'SUV'];
  const limitedTowing50kmBodyTypes = ['TAXI'];
  const limitedTowing50km3TonsBodyTypes = ['MINIVAN', 'VAN'];
  const isPickupUpTo2Tons = bodyType === 'PICKUP' && Number(weight) <= 2;

  let desiredKeys: string[] = [];

  if (unlimitedTowingBodyTypes.includes(bodyType) || isPickupUpTo2Tons) {
    desiredKeys = ['UnlimitedTowing'];
  } else if (limitedTowing50kmBodyTypes.includes(bodyType)) {
    desiredKeys = ['LimitedTowing50km'];
  } else if (limitedTowing50km3TonsBodyTypes.includes(bodyType)) {
    desiredKeys = ['LimitedTowing50km3Tons'];
  } else {
    desiredKeys = ['NA'];
  }

  // LimitedTowing is never offered in the UI (backend may still return it).

  return Object.fromEntries(
    Object.entries(towingServices).filter(([key]) => desiredKeys.includes(key))
  );
}

const breakdown2UpTo100km50KmModels = [
  'Brand25Model463',
  'Brand25Model1019',
  'Brand25Model1061',
  'Brand25Model1117',
  'Brand25Model1121',
  'Brand25Model1129',
  'Brand25Model1137',
  'Brand25Model1142',
  'Brand25Model1143',
  'Brand25Model1147',
  'Brand25Model1152',
  'Brand25Model535',
  'Brand25Model1103',
  'Brand25Model1126',
  'Brand42Model188',
  'Brand28Model135',
];

const breakdown2UpTo100km50KmBrand = 'Brand5';

export const isBreakdown2UpTo100km50KmModel = (model = '') =>
  breakdown2UpTo100km50KmModels.includes(model);

export const isBreakdown2UpTo100km50KmBrand = (brand = '') =>
  brand === breakdown2UpTo100km50KmBrand;

export const isBreakdown2UpTo100km50KmEligible = (model = '', brand = '') =>
  isBreakdown2UpTo100km50KmModel(model) ||
  isBreakdown2UpTo100km50KmBrand(brand);

export function filterBreakdownServicesLOVs(
  bodyType: string,
  breakdownServices: Record<string, string>,
  weight = 0,
  model = '',
  brand = ''
) {
  if (!breakdownServices) {
    return breakdownServices;
  }

  const standardBreakdownBodyTypes = ['4x4', 'SEDAN', 'STATION', 'SUV'];
  const limitedBreakdownBodyTypes = ['TAXI', 'MINIVAN', 'VAN'];
  const isPickup = bodyType === 'PICKUP';
  const isPickupUpTo2Tons = isPickup && Number(weight) <= 2;
  const isPickupSpecialBreakdown =
    isPickup && isBreakdown2UpTo100km50KmEligible(model, brand);
  const isPickupLimitedBreakdown =
    isPickupUpTo2Tons && !isPickupSpecialBreakdown;

  let desiredKeys: string[] = [];

  if (isPickupSpecialBreakdown) {
    desiredKeys = ['2UpTo100km50Km'];
  } else if (standardBreakdownBodyTypes.includes(bodyType)) {
    desiredKeys = ['None', '1UpTo75km', '2Unlimited'];
  } else if (
    limitedBreakdownBodyTypes.includes(bodyType) ||
    isPickupLimitedBreakdown
  ) {
    desiredKeys = ['1UpTo35km'];
  }

  return Object.fromEntries(
    Object.entries(breakdownServices).filter(([key]) =>
      desiredKeys.includes(key)
    )
  );
}

//method need to return true to avoid phone number validation in case phone number is empty
export const checkPhoneNumber = (phoneNumber: string) => {
  //need to check that we are not receiving the country code also
  if (!isEmpty(phoneNumber) && phoneNumber.length > 4) {
    return validatePhoneNumber(phoneNumber);
  }

  return false;
};

export const checkQuoteName = (
  quoteName: string | number | NonNullable<unknown>
) => {
  if (!isEmpty(quoteName) || quoteName !== '' || quoteName !== null) {
    return true;
  } else {
    return 'Required';
  }
};

export const checkEmail = (email: string) => {
  if (!isEmpty(email)) {
    return isValidEmail(email);
  }

  return true;
};

export const getApplicationStatus = (status: string) => {
  switch (status) {
    case 'NEW':
      return steps[0];
    case 'QUOTE_PREPARATION':
      return steps[1];
    case 'QUOTE_CONFIRMATION':
      return steps[2];
    case 'CLOSED':
      return steps[3];
    default:
      return steps[0];
  }
};

export const getApplicationProgress = (status: string) => {
  switch (status) {
    case 'NEW':
      return 25;
    case 'QUOTE_PREPARATION':
      return 50;
    case 'QUOTE_CONFIRMATION':
      return 57;
    case 'CLOSED':
      return 100;
    default:
      return 0;
  }
};

export const isVisiblePlanDetailsField = (
  product: string,
  planDetailsFieldName: string
): boolean => {
  let isVisible = false;
  product = product?.toString()?.toLowerCase();
  planDetailsFieldName = planDetailsFieldName.toString().toLowerCase();
  if (product == 'allrisk') {
    isVisible =
      planDetailsFieldName == 'coveragetype' ||
      planDetailsFieldName == 'carvalue' ||
      planDetailsFieldName == 'includeagencyrepair';
  } else if (product == 'bodilyinjury') {
    isVisible = planDetailsFieldName == 'vignette';
  } else if (product == 'materialdamage') {
    isVisible =
      planDetailsFieldName == 'towingservice' ||
      planDetailsFieldName == 'breakdownservices';
  } else if (product == 'totalloss') {
    isVisible = planDetailsFieldName == 'carvalue';
  }

  return isVisible;
};

const breakdownEligibleBodyTypes = [
  '4x4',
  'SEDAN',
  'STATION',
  'SUV',
  'TAXI',
  'MINIVAN',
  'VAN',
];

export const isVisibleBreakdownServicesField = (
  product: string,
  bodyType: string,
  weight = 0,
  model = '',
  brand = ''
): boolean => {
  if (!isVisiblePlanDetailsField(product, 'breakdownServices')) {
    return false;
  }

  if (
    bodyType === 'PICKUP' &&
    isBreakdown2UpTo100km50KmEligible(model, brand)
  ) {
    return true;
  }

  if (breakdownEligibleBodyTypes.includes(bodyType)) {
    return true;
  }

  return bodyType === 'PICKUP' && Number(weight) <= 2;
};

export function filterUsageTypeLOVs(
  plateCode = '',
  usageType: Record<string, string>,
  carNationality = ''
) {
  let desiredKeys = ['Public'];

  if (usageType) {
    if (plateCode !== 'P') {
      desiredKeys = [...desiredKeys, 'Private'];
    }

    // Commercial: PlateCode <> "P" AND CarNationality = Lebanese
    // Empty nationality (non–Cash Plus) keeps prior Commercial LOV behavior.
    if (
      plateCode !== 'P' &&
      (!carNationality || carNationality === DEFAULT_CAR_NATIONALITY)
    ) {
      desiredKeys = [...desiredKeys, 'Commercial'];
    }

    return Object.fromEntries(
      Object.entries(usageType).filter(([key]) => desiredKeys.includes(key))
    );
  }

  return {};
}

export function filterProductsByAgentProductsLOVS(
  mainProducts: Record<string, string>,
  agentProducts: string[]
) {
  if (agentProducts) {
    return Object.fromEntries(
      Object.entries(mainProducts).filter(([key]) =>
        agentProducts.includes(key)
      )
    );
  }

  return {};
}

export function adjustAgencyRepair(
  carValue: number,
  agencyRepair: Record<string, string>
) {
  let desiredKeys: string[] = ['YES'];

  if (carValue && carValue <= 100000) {
    desiredKeys = [...desiredKeys, 'NO'];
  }

  return Object.fromEntries(
    Object.entries(agencyRepair).filter(([key]) => desiredKeys.includes(key))
  );
}
export function canShowIncludeAgencyRepairField(
  coverageType: string,
  yearOfMake: number,
  usageType: string
) {
  return (
    usageType == 'Private' &&
    yearOfMake >= 2021 &&
    yearOfMake <= 2024 &&
    coverageType == 'LUX_PLAN'
  );
}
